import React, { FC, ReactNode, RefObject } from 'react';
import StateManager, {
  components as ReactSelectComponents,
} from 'react-select';
import { FieldOptionBase } from 'api/tasks';
import { SelectMode } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { ClassNameOnlyProps } from 'components/ozone-component';
import { Popover, Select } from './components';

type OnChangeType<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = IsMulti extends true
  ? (value?: FieldOptionBase<OptionValueType>[]) => void
  : (value?: FieldOptionBase<OptionValueType>) => void;

export type ValueType<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = IsMulti extends true
  ? FieldOptionBase<OptionValueType>[]
  : FieldOptionBase<OptionValueType> | undefined;

export type OptionGroup<OptionValueType extends number | string = number> = {
  label: string;
  options: FieldOptionBase<OptionValueType>[];
};
type OptionsType<OptionValueType extends number | string = number> =
  | OptionGroup<OptionValueType>[]
  | FieldOptionBase<OptionValueType>[];

export type Props<
  IsMulti extends boolean,
  OptionValueType extends number | string = number,
> = ClassNameOnlyProps & {
  title: ReactNode;
  value: ValueType<IsMulti, OptionValueType>;
  onChange: OnChangeType<IsMulti, OptionValueType>;
  selectRef: RefObject<StateManager>;
  options: OptionsType<OptionValueType>;
  components: Partial<typeof ReactSelectComponents>;
  isMulti: IsMulti;
  type: SelectMode;
  grouped?: boolean;
};

export const getControlledSelectBasedEditorPopover =
  <
    IsMulti extends boolean,
    OptionValueType extends number | string = number,
  >(): FC<Props<IsMulti, OptionValueType>> =>
  ({
    title,
    onChange,
    selectRef,
    className,
    options,
    components,
    isMulti,
    value,
    type,
    grouped = false,
  }) =>
    (
      <Popover className={className}>
        <Popover.Title>{title}</Popover.Title>
        <Select
          components={{ MenuList: VirtualizedMenuList, ...components }}
          isMulti={isMulti}
          ref={selectRef}
          options={options}
          value={value}
          onChange={onChange}
          type={type}
          grouped={grouped}
        />
      </Popover>
    );
